<script lang="ts">
  /**
   * The round profile button at the top right of every tab: the player's
   * picture (TwinTen ADR 0073), else their initial, else a generic person for
   * a guest, opening the profile sheet. `showName` adds the name beside it —
   * the desktop sidebar's footer has the room a phone's top bar does not.
   */
  import Avatar from './Avatar.svelte';

  interface Props {
    readonly initial: string | null;
    /** The button's accessible name ("Your profile and settings"). */
    readonly label: string;
    readonly onclick: () => void;
    readonly name?: string | undefined;
    readonly showName?: boolean;
    /** The player's picture (`Avatar`'s `src`); absent draws the initial. */
    readonly src?: string | null | undefined;
    /**
     * `dialog` (the default): it opens a sheet. `false`: it navigates (a game
     * whose account entry is a page), so no popup is announced.
     */
    readonly haspopup?: 'dialog' | false;
    readonly testid?: string | undefined;
  }
  const {
    initial,
    label,
    onclick,
    name,
    showName = false,
    src,
    haspopup = 'dialog',
    testid,
  }: Props = $props();
</script>

<button
  type="button"
  class="ui-avatar-button"
  class:ui-avatar-button--named={showName && name !== undefined}
  aria-label={label}
  aria-haspopup={haspopup === false ? undefined : haspopup}
  data-testid={testid}
  {onclick}
>
  <span class="ui-avatar-circle" aria-hidden="true">
    <Avatar name={name ?? ''} {initial} {src} size="md" tone="accent" />
  </span>
  {#if showName && name !== undefined}
    <span class="ui-avatar-name" aria-hidden="true">{name}</span>
  {/if}
</button>

<style>
  .ui-avatar-button {
    display: inline-flex;
    align-items: center;
    gap: var(--ui-space-3);
    min-width: 2.75rem;
    min-height: 2.75rem;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-avatar-button--named {
    width: 100%;
    padding: 0.3rem 0.5rem;
    border-radius: var(--ui-radius-md);
  }

  .ui-avatar-button:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  .ui-avatar-circle {
    flex: 0 0 auto;
    display: inline-flex;
  }

  .ui-avatar-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font: var(--ui-text-body);
    font-weight: 800;
  }
</style>
